import { registerBulkUsers } from '../data/api';
import { logStudioHomeError } from '../logging';

/**
 * Alphabetical sorting for arrays of courses and libraries.
 *
 * @param {array} arr - Array of courses or libraries.
 * @returns {array} - An array of alphabetically sorted courses or libraries.
 */
const sortAlphabeticallyArray = (arr) => [...arr]
  .sort((firstArrayData, secondArrayData) => {
    const firstDisplayName = firstArrayData.displayName ?? '';
    const secondDisplayName = secondArrayData.displayName ?? '';
    return firstDisplayName.localeCompare(secondDisplayName);
  });

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email is valid
 */
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate zip code format (US zip codes)
 * @param {string} zipCode - Zip code to validate
 * @returns {boolean} - True if zip code is valid
 */
const validateZipCode = (zipCode) => {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zipCode);
};

/**
 * Validate US state code (2-letter abbreviation)
 * @param {string} state - State code to validate
 * @returns {boolean} - True if state code is valid
 */
const validateStateCode = (state) => {
  const validStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC', 'PR', 'VI', 'GU', 'AS', 'MP', // Include DC and territories
  ];
  return validStates.includes(state.toUpperCase().trim());
};

/**
 * Check if a field name is a state field
 * @param {string} fieldName - Field name to check
 * @returns {boolean} - True if field is a state field
 */
const isStateField = (fieldName) => fieldName.toLowerCase() === 'state';

/**
 * Check if a field name is a zip code field (supports multiple variants)
 * @param {string} fieldName - Field name to check
 * @returns {boolean} - True if field is a zip code field
 */
const isZipCodeField = (fieldName) => {
  const normalized = fieldName.toLowerCase().replace(/\s+/g, '');
  return ['zip', 'zipcode', 'postalcode', 'postcode'].includes(normalized);
};

/**
 * Generate a random password for new users
 * @param {number} length - Length of password (default 12)
 * @returns {string} - Generated password
 */
const generatePassword = (length = 12) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

/**
 * Check if user already exists (placeholder)
 * TODO: Implement actual API call to check user existence
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if user exists
 */
const checkUserExists = async () => false;

/**
 * Create learner profiles (new users or update existing)
 * This function will create new user profiles and/or update existing ones
 *
 * @param {Object} params - Parameters object
 * @param {string} params.companyName - Company name/org identifier
 * @param {string} params.companyFriendlyName - Friendly name of company
 * @param {Array} params.learners - Array of learner objects
 * @param {Array} params.licenseOptions - Array of license options with id and type
 * @returns {Promise<Object>} - Result with created/updated users and any errors
 */
const createLearnerProfiles = async ({
  companyName, companyFriendlyName, learners, licenseOptions,
}) => {
  // Create a lookup map for license types and full license data
  const licenseMap = {};
  if (licenseOptions) {
    licenseOptions.forEach(option => {
      licenseMap[option.id] = option;
    });
  }

  // Transform learners to the required API format
  const employees = learners.map(learner => {
    // Separate selected licenses into courses, pathways, and legacy licenses
    const deferredEnrollments = [];
    const programsPathways = [];
    const legacyLicenses = [];

    if (learner.selectedLicenses && learner.selectedLicenses.length > 0) {
      learner.selectedLicenses.forEach(licenseId => {
        const license = licenseMap[licenseId];
        if (!license) { return; }

        if (license.type === 'program') {
          // This is a pathway/program
          programsPathways.push(licenseId);
          deferredEnrollments.push({ enroll_id: licenseId, pk_id: license.pk_id });
        } else if (license.type === 'legacy_bundle') {
          // This is a legacy bundle
          // Add the bundle name to legacy_licenses
          legacyLicenses.push(licenseId);
          // Add all course IDs from the bundle's course_list to deferred_enrollments
          if (license.course_list && license.course_list.length > 0) {
            deferredEnrollments.push(...license.course_list.map(courseId => ({ enroll_id: courseId })));
          }
        } else if (license.type === 'catalog_course') {
          // This is a catalog course
          deferredEnrollments.push({ enroll_id: licenseId, pk_id: license.pk_id });
        }
      });
    }

    // Determine if THIS learner is using catalog system
    // is_company_catalog should be true if the learner has any catalog courses or programs
    // is_company_catalog should be false if the learner has ONLY legacy bundles
    const hasAnyCatalogOrProgram = learner.selectedLicenses && learner.selectedLicenses.length > 0
      && learner.selectedLicenses.some(licenseId => {
        const license = licenseMap[licenseId];
        return license && (license.type === 'catalog_course' || license.type === 'program');
      });

    return {
      email: learner.email,
      first_name: learner.firstName,
      last_name: learner.lastName,
      password: generatePassword(),
      is_alumni: false,
      must_update_pass: true,
      enterprise: {
        company_name: companyName,
        friendly_name: companyFriendlyName,
        is_admin: false,
        metadata: learner.customFields || {},
        deferred_enrollments: deferredEnrollments,
        programs_pathways: programsPathways,
        legacy_licenses: legacyLicenses,
        is_company_catalog: hasAnyCatalogOrProgram,
        edx_course_org: companyName,
      },
    };
  });

  // Build the payload
  const payload = {
    b2b_org: companyName,
    employees,
  };

  try {
    // Call the bulk registration API
    const response = await registerBulkUsers(payload);

    // Transform response to expected format
    return {
      success: true,
      created: response.created || [],
      existing: response.existing || [],
      errors: response.errors || [],
      taskId: response.bulk_register_users_manager_task_id || response.task_id || response.taskId || null,
    };
  } catch (error) {
    logStudioHomeError('studio_home_learner_profiles_create_failed', error, {
      learnerCount: learners.length,
    });
    throw error;
  }
};

/**
 * Parse CSV file content into a 2D array
 * Handles quoted fields, escaped quotes, and different line endings
 * @param {string} text - CSV file content as string
 * @returns {string[][]} - 2D array of parsed CSV data
 */
const parseCSV = (text) => {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      // Row separator
      if (char === '\r' && nextChar === '\n') {
        i++; // Skip \n in \r\n
      }
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        if (currentRow.some(field => field)) {
          // Only add row if it has at least one non-empty field
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = '';
      }
    } else {
      currentField += char;
    }
  }

  // Add last field and row if any
  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(field => field)) {
      rows.push(currentRow);
    }
  }

  return rows;
};

/**
 * Download a CSV file to the user's browser
 * @param {string} content - CSV content as string
 * @param {string} filename - Name for the downloaded file
 */
const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Generate a CSV template for bulk learner upload
 * @param {string[]} customFields - Array of custom field names
 * @param {Array<{name: string}>} licenseOptions - Array of license options with name property
 * @returns {string} - CSV content as string
 */
const generateCSVTemplate = (customFields, licenseOptions) => {
  const headers = [
    'First Name',
    'Last Name',
    'Email',
  ];

  // Add custom fields
  if (customFields && customFields.length > 0) {
    headers.push(...customFields);
  }

  // Add license columns
  licenseOptions.forEach(license => {
    headers.push(license.name);
  });

  // Create CSV content with headers and sample row
  const sampleRow = [
    'John',
    'Doe',
    'john.doe@example.com',
  ];

  // Add sample custom fields
  if (customFields && customFields.length > 0) {
    customFields.forEach((field) => {
      // Add sample values based on field type
      if (isStateField(field)) {
        sampleRow.push('CA');
      } else if (isZipCodeField(field)) {
        sampleRow.push('12345');
      } else {
        sampleRow.push('Sample Value');
      }
    });
  }

  // Add sample license values
  licenseOptions.forEach(() => {
    sampleRow.push('FALSE');
  });

  const csvContent = [
    headers.map(h => `"${h}"`).join(','),
    sampleRow.map(v => `"${v}"`).join(','),
  ].join('\n');

  return csvContent;
};

/**
 * Validate a single learner row from CSV
 * @param {string[]} row - Array of field values from CSV row
 * @param {string[]} headers - Array of header names
 * @param {number} rowNumber - Row number for error reporting
 * @param {Set<string>} existingEmails - Set of existing emails (lowercase)
 * @param {string[]} customFields - Array of custom field names
 * @param {Array<{id: string, name: string}>} licenseOptions - Array of license options
 * @returns {{learner: Object, errors: Object} | null} - Learner data and validation errors, or null if malformed
 */
const validateCSVLearner = (
  row,
  headers,
  rowNumber,
  existingEmails,
  customFields,
  licenseOptions,
) => {
  if (row.length !== headers.length) {
    return null; // Skip rows with wrong column count
  }

  const learner = {
    firstName: '',
    lastName: '',
    email: '',
    customFields: {},
    selectedLicenses: [],
  };

  const errors = {};
  let hasError = false;

  // Map column values to learner fields
  headers.forEach((header, index) => {
    const value = row[index] || '';

    if (header === 'First Name') {
      learner.firstName = value;
      if (!value.trim()) {
        errors.firstName = 'First name is required';
        hasError = true;
      }
    } else if (header === 'Last Name') {
      learner.lastName = value;
      if (!value.trim()) {
        errors.lastName = 'Last name is required';
        hasError = true;
      }
    } else if (header === 'Email') {
      learner.email = value;
      if (!value.trim()) {
        errors.email = 'Email is required';
        hasError = true;
      } else if (!validateEmail(value)) {
        errors.email = 'Invalid email format';
        hasError = true;
      } else if (existingEmails.has(value.toLowerCase())) {
        errors.email = 'Duplicate email';
        hasError = true;
      }
    } else if (customFields && customFields.includes(header)) {
      // Custom field with conditional validation for state/zip code
      learner.customFields[header] = value;

      // Check if this is a state field
      if (isStateField(header)) {
        if (!value.trim()) {
          if (!errors.customFields) { errors.customFields = {}; }
          errors.customFields[header] = `${header} is required`;
          hasError = true;
        } else if (!validateStateCode(value)) {
          if (!errors.customFields) { errors.customFields = {}; }
          errors.customFields[header] = 'Invalid state code (use 2-letter abbreviation like CA, NY)';
          hasError = true;
        }
      } else if (isZipCodeField(header)) {
        if (!value.trim()) {
          if (!errors.customFields) { errors.customFields = {}; }
          errors.customFields[header] = `${header} is required`;
          hasError = true;
        } else if (!validateZipCode(value)) {
          if (!errors.customFields) { errors.customFields = {}; }
          errors.customFields[header] = 'Invalid zip code format (e.g., 12345 or 12345-6789)';
          hasError = true;
        }
      } else if (!value.trim()) {
        if (!errors.customFields) { errors.customFields = {}; }
        errors.customFields[header] = `${header} is required`;
        hasError = true;
      }
    } else {
      // License column - check if it's a license name
      const license = licenseOptions.find(l => l.name === header);
      if (license) {
        const normalizedValue = value.trim().toUpperCase();
        if (normalizedValue === 'TRUE') {
          learner.selectedLicenses.push(license.id);
        } else if (normalizedValue !== 'FALSE' && normalizedValue !== '') {
          hasError = true;
          // Note: We don't have a specific field for license errors
        }
      }
    }
  });

  return { learner, errors: hasError ? errors : {} };
};

export {
  sortAlphabeticallyArray,
  validateEmail,
  validateZipCode,
  validateStateCode,
  isStateField,
  isZipCodeField,
  checkUserExists,
  createLearnerProfiles,
  generatePassword,
  parseCSV,
  downloadCSV,
  generateCSVTemplate,
  validateCSVLearner,
};
